import { useEffect, useRef } from 'react';

import { paletteState } from 'core/state';
import { isEditableTarget } from 'core/utils/isEditableTarget';
import { isGoToArmed } from 'core/utils/goToSequence';

/** Клавиши почты (§5) — как в Gmail. */
export type MailHotkey =
  | 'next'
  | 'prev'
  | 'open'
  | 'done'
  | 'trash'
  | 'star'
  | 'reply'
  | 'replyAll'
  | 'forward'
  | 'compose'
  | 'search'
  | 'back'
  | 'task';

export const MAIL_HOTKEYS: Array<{ key: string; action: MailHotkey }> = [
  { key: 'j', action: 'next' },
  { key: 'k', action: 'prev' },
  { key: 'Enter', action: 'open' },
  { key: 'o', action: 'open' },
  { key: 'e', action: 'done' },
  { key: '#', action: 'trash' },
  { key: 's', action: 'star' },
  { key: 'r', action: 'reply' },
  { key: 'a', action: 'replyAll' },
  { key: 'f', action: 'forward' },
  { key: 'c', action: 'compose' },
  { key: '/', action: 'search' },
  { key: 'u', action: 'back' },
  { key: 't', action: 'task' },
];

const CHORD_MS = 1200;

/** Клавиша → действие почты (`null` — не наша клавиша). */
export const mailHotkeyFor = (key: string): MailHotkey | null =>
  MAIL_HOTKEYS.find((item) => item.key === key)?.action ?? null;

/**
 * ТЗ 0122 — клавиши страницы «Почта» без модификаторов. Молчат, пока
 * набирается текст, открыта палитра, окно (`blocked`) или идёт аккорд
 * `g → раздел` (вторая клавиша аккорда — не наша: `g s` — студенты, а не
 * «звезда»).
 */
export const useMailHotkeys = (
  handler: (action: MailHotkey, event: KeyboardEvent) => void,
  blocked: boolean,
) => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const blockedRef = useRef(blocked);
  blockedRef.current = blocked;

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    let lastG = 0;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (blockedRef.current || paletteState().open) return;
      if (isEditableTarget(event.target)) return;
      if (document.querySelector('.ant-modal-wrap:not([style*="none"])')) {
        return;
      }
      const now = Date.now();
      if (event.key === 'g') {
        lastG = now;
        return;
      }
      if (isGoToArmed() || now - lastG < CHORD_MS) {
        lastG = 0;
        return;
      }
      // Enter на кнопке / ссылке — её собственное нажатие.
      if (
        event.key === 'Enter' &&
        event.target instanceof HTMLElement &&
        event.target.closest('button, a, [role="listitem"]')
      ) {
        return;
      }
      const action = mailHotkeyFor(event.key);
      if (!action) return;
      event.preventDefault();
      handlerRef.current(action, event);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
};
